"use client";

import { useMemo } from "react";

import { useWidgetAggregateSeries } from "@/hooks/useWidgetAggregateSeries";

import { buildCreateAlertFromWidgetHref } from "@/modules/alerts/utils/create-alert-from-widget";

import { WidgetChrome } from "./WidgetChrome";
import type { WidgetShellProps } from "./types";

function latestValue(points: { values: Record<string, number | null> }[], channel: string): number | null {
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const v = points[i]!.values[channel];
    if (v != null && typeof v === "number" && !Number.isNaN(v)) {
      return v;
    }
  }
  return null;
}

export function GaugeWidget({ widget, config, timeRange, customRange, hasActiveAlert }: WidgetShellProps) {
  const seriesKey = config.series_key;

  const { data, isLoading, error } = useWidgetAggregateSeries(widget.id, widget.type, {
    deviceId: config.device_id,
    channel: seriesKey,
    timeRange,
    customRange,
    aggregation: config.aggregation,
    refreshIntervalSeconds: config.refresh_interval_seconds,
  });

  const { value, min, max, pct } = useMemo(() => {
    const pts = data?.points ?? [];
    const v = latestValue(pts, seriesKey);
    let lo = config.min_value;
    let hi = config.max_value;
    if (lo == null || hi == null) {
      let vmin = Infinity;
      let vmax = -Infinity;
      for (const p of pts) {
        const x = p.values[seriesKey];
        if (x != null && typeof x === "number" && !Number.isNaN(x)) {
          vmin = Math.min(vmin, x);
          vmax = Math.max(vmax, x);
        }
      }
      if (lo == null) {
        lo = Number.isFinite(vmin) ? vmin : 0;
      }
      if (hi == null) {
        hi = Number.isFinite(vmax) ? vmax : lo + 1;
      }
    }
    if (hi <= lo) {
      hi = lo + 1;
    }
    const raw = v == null ? null : ((v - lo) / (hi - lo)) * 100;
    const clamped = raw == null ? null : Math.min(100, Math.max(0, raw));
    return { value: v, min: lo, max: hi, pct: clamped };
  }, [data, config.min_value, config.max_value, seriesKey]);

  const title =
    config.title?.trim() || config.formula?.trim() || config.metric_id?.slice(0, 8) || config.channel || "Gauge";

  return (
    <WidgetChrome
      widget={widget}
      title={title}
      subtitle="Gauge · range from config or series"
      isLoading={isLoading && !data}
      error={error}
      createAlertHref={buildCreateAlertFromWidgetHref(config)}
      hasActiveAlert={hasActiveAlert}
    >
      {!error && pct == null ? (
        <p className="text-caption text-muted">No samples in this window.</p>
      ) : !error && pct != null ? (
        <div className="flex flex-col gap-ds-4 py-ds-1">
          <div
            className="h-3 w-full overflow-hidden rounded-full bg-muted-bg"
            role="meter"
            aria-valuenow={Math.round(pct)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Gauge ${title}`}
          >
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold tabular-nums text-foreground">
              {value != null ? value.toFixed(2) : "—"}
            </p>
            <p className="mt-ds-1 text-caption text-muted">
              {min.toFixed(1)} → {max.toFixed(1)}
            </p>
          </div>
        </div>
      ) : null}
    </WidgetChrome>
  );
}
